import React, { useEffect, useMemo, useState } from "react";
import { FiEye } from "react-icons/fi";
import Lozenge from "../components/BaseUI/Lozenge";
import Pagination from "../components/BaseUI/Pagination";
import ApprovalAttachmentsModal from "../components/CustomUI/ApprovalAttachmentsModal";
import { formatInstantDateShortTr } from "../utils/appTime";
import "./plugins/order/orderDetail.css";

const ACTION_META = {
  approved: { label: "Onaylandı", appearance: "success-subtle" },
  rejected: { label: "Reddedildi", appearance: "danger-subtle" },
  user_confirmed: { label: "Kullanıcı onayı", appearance: "info-subtle" },
};

const LOGS_PER_PAGE = 1;

const ApprovalStepLogsTable = ({ logs = [] }) => {
  const [page, setPage] = useState(1);
  const [previewAttachments, setPreviewAttachments] = useState(null);
  const [previewAttachmentId, setPreviewAttachmentId] = useState(null);

  const safeLogs = useMemo(
    () => [...(Array.isArray(logs) ? logs : [])].reverse(),
    [logs],
  );

  useEffect(() => {
    setPage(1);
  }, [safeLogs]);

  const totalPages = Math.max(1, Math.ceil(safeLogs.length / LOGS_PER_PAGE) || 1);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedLogs = useMemo(() => {
    const start = (page - 1) * LOGS_PER_PAGE;
    return safeLogs.slice(start, start + LOGS_PER_PAGE);
  }, [safeLogs, page]);

  if (safeLogs.length === 0) return null;

  const openAttachmentPreview = (log, attachment) => {
    setPreviewAttachments(
      (log.attachments || []).map((item) => ({
        ...item,
        stepLabel: log.stepLabel || log.stepKey || "",
        resolvedBy: log.resolvedBy?.name || "",
        resolvedAt: log.resolvedAt || null,
      })),
    );
    setPreviewAttachmentId(attachment.id);
  };

  return (
    <div className="detail-section mb-0">
      <strong>Onay Geçmişi</strong>
      <div className="d-flex flex-column gap-2 mt-2">
        {pagedLogs.map((log, index) => {
          const actionMeta = ACTION_META[log.action] || {
            label: log.action || "-",
            appearance: "neutral-subtle",
          };

          return (
            <div
              key={`${log.stepKey || "log"}-${(page - 1) * LOGS_PER_PAGE + index}`}
              className="order-detail-grid"
            >
              <div className="detail-row">
                <span className="detail-label">Adım</span>
                <span>{log.stepLabel || log.stepKey || "-"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">İşlem</span>
                <Lozenge appearance={actionMeta.appearance}>
                  {actionMeta.label}
                </Lozenge>
              </div>
              <div className="detail-row">
                <span className="detail-label">Onaylayan</span>
                <span>{log.resolvedBy?.name || "-"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Tarih</span>
                <span>
                  {log.resolvedAt
                    ? formatInstantDateShortTr(log.resolvedAt)
                    : "-"}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Not</span>
                <span className="text-muted">{log.reason || "-"}</span>
              </div>
              {log.attachments?.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Dosya</span>
                  <div className="d-flex flex-column gap-1">
                    {log.attachments.map((attachment) => (
                      <button
                        key={attachment.id}
                        type="button"
                        className="btn btn-link p-0 text-start"
                        onClick={() => openAttachmentPreview(log, attachment)}
                      >
                        <FiEye className="me-1" />
                        {attachment.originalName}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {safeLogs.length > 1 && (
        <div className="mt-2">
          <Pagination
            totalItems={safeLogs.length}
            totalPerPage={LOGS_PER_PAGE}
            currentPage={page}
            totalRecords={safeLogs.length}
            options={{ selectQuantity: false }}
            onPageChange={({ pagination }) => {
              setPage(pagination.currentPage);
            }}
          />
        </div>
      )}

      <ApprovalAttachmentsModal
        isOpen={!!previewAttachments}
        onClose={() => {
          setPreviewAttachments(null);
          setPreviewAttachmentId(null);
        }}
        attachments={previewAttachments || []}
        initialAttachmentId={previewAttachmentId}
        title="Dosya Önizleme"
      />
    </div>
  );
};

export default ApprovalStepLogsTable;
